import React from 'react';

import { Icon } from '@rover/icons';
import {
  type FontWeightProps,
  type TextAlignProps,
  type TextTransformProps,
  type ThemeHeadingSizes,
  type ThemeSpacing,
  type ThemeTextDecoration,
  type ThemeTextSizes,
} from '@rover/kibble/styles';

import type { SpacingSystemProps } from '../styledSystemPropTypes';

/** Link visual style variants */
export type LinkVariant =
  | 'primary'
  | 'secondary'
  | 'primaryContrast'
  | 'secondaryContrast'
  | 'navigation';

export type LinkCommonProps = SpacingSystemProps &
  FontWeightProps &
  TextAlignProps &
  TextTransformProps & {
    /** Link content (text or elements) */
    children: NonNullable<React.ReactNode>;
    /** Icon component to display alongside link text */
    icon?: Icon;
    /** Position of icon relative to text */
    iconAlignment?: 'left' | 'right';
    /** Theme sizes, both Text and Heading sizes are valid */
    size?: ThemeTextSizes | ThemeHeadingSizes;
    /** Show underline decoration */
    underline?: boolean;
    /** Visual style variant */
    variant?: LinkVariant;
    /** ID for finding this element in unit tests */
    testID?: string;
    /** Prefer using TextWithSingleInlineLink on RxN. Render link inline with text, icons not compatible on RxN */
    asInlineLink?: true;
    /** Link target attribute, web-only */
    target?: '_blank' | '_self' | '_parent' | '_top' | string;
  };

export type LinkVariantMap<T> = {
  [P in LinkVariant]: T;
};

export const LinkColorMap: LinkVariantMap<string> = {
  primary: 'interactive_link.primary_base',
  primaryContrast: 'interactive_link.primary_inverse_base',
  secondary: 'interactive_link.secondary_base',
  secondaryContrast: 'interactive_link.secondary_inverse_base',
  navigation: 'text.navigation', // used in NavBar
};

export const LinkHoverColorMap: LinkVariantMap<string> = {
  primary: 'interactive_link.primary_hover',
  primaryContrast: 'interactive_link.primary_inverse_hover',
  secondary: 'interactive_link.secondary_hover',
  secondaryContrast: 'interactive_link.secondary_inverse_hover',
  navigation: 'text.navigation_hover', // used in NavBar
};

export type LinkSizeMap<T> = {
  '50': T;
  '100': T;
  '200': T;
  '300': T;
  '400': T;
  '500': T;
  '600': T;
};

// maps icon size based on text size
export const IconSizeMap: LinkSizeMap<string> = {
  '50': '16px',
  '100': '16px',
  '200': '16px',
  '300': '16px',
  '400': '20px',
  '500': '24px',
  '600': '24px',
};

// used on RxN to recreate Heading bottom margins
export const BottomMarginMap: LinkSizeMap<ThemeSpacing> = {
  '50': '0x',
  '100': '0x',
  '200': '0x',
  '300': '0x',
  '400': '5x',
  '500': '6x',
  '600': '8x',
};

export type TypographyComponentProps<StylePropName extends 'sx' | 'style'> = {
  textDecoration: ThemeTextDecoration; // controls underline

  mb?: '0x'; // used on RxN to suppress Heading margin bottoms that mess with flexbox aligning the icon
} & {
  [P in StylePropName]: {
    color: string; // text color

    textDecorationColor?: string; // used on RxN to control underline color

    display?: P extends 'sx' ? 'inline' | 'inline-flex' : undefined; // on web we use inline for props.useInlineHeadingHack, on native we don't use this
    alignItems?: 'center'; // used on web to align icon with link text
    '&:hover'?: { color: string; textDecoration: string }; // used on web for hover styles
  };
};

export const getIsTextSized = (size: ThemeTextSizes | ThemeHeadingSizes): size is ThemeTextSizes =>
  size === '50' || size === '100' || size === '200' || size === '300';
